/**
 * Auto-advancing USP bar. Kicks in only when the items no longer fit on one
 * row: the labels are `white-space: nowrap`, so a label wider than its grid
 * column is the signal that we ran out of room.
 *
 * Movement is stepped - one item per tick, not a continuous crawl - and the
 * active item sits centred in the viewport. The list is rendered three times so
 * there is always a copy either side of centre; once the track lands on the
 * trailing copy's first item it snaps back without a transition, so the loop
 * reads as endless.
 */
class UspBarMarquee extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-usp-viewport]');
    this.track = this.querySelector('[data-usp-track]');
    this.list = this.querySelector('[data-usp-list]');

    if (!this.viewport || !this.track || !this.list) return;

    this.index = 0;
    this.metrics = [];

    this.measure = this.measure.bind(this);
    this.advance = this.advance.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);

    this.track.addEventListener('transitionend', this.onTransitionEnd);
    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());

    this.resize = new ResizeObserver(this.measure);
    this.resize.observe(this.viewport);

    // No point stepping through a bar nobody is looking at
    this.visibility = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.start();
      else this.stop();
    });
    this.visibility.observe(this);

    this.measure();
  }

  disconnectedCallback() {
    this.stop();
    this.resize?.disconnect();
    this.visibility?.disconnect();
    this.track?.removeEventListener('transitionend', this.onTransitionEnd);
  }

  /** Seconds each item stays put before the next one slides in. */
  get interval() {
    return (Number(this.dataset.interval) || 3) * 1000;
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  measure() {
    const width = this.viewport.clientWidth;

    // Switching modes changes the height, which re-triggers the observer.
    // Only width can change the outcome, so ignore everything else.
    if (width === this.lastWidth) return;
    this.lastWidth = width;

    this.stop();
    this.index = 0;

    // Measure in the static row layout, otherwise we would be measuring the
    // slider we are trying to decide on.
    this.removeAttribute('data-marquee');
    this.moveTo(0, true);

    const overflows = Array.from(this.list.children).some((item) => {
      const inner = item.firstElementChild;
      return inner ? inner.scrollWidth > item.clientWidth + 1 : false;
    });

    if (!overflows) return;

    this.setAttribute('data-marquee', '');

    // Item widths vary with label length, so step by measured geometry
    // rather than a fixed amount.
    const items = Array.from(this.track.querySelectorAll('.usp-bar__item'));
    const origin = items[0]?.offsetLeft ?? 0;

    this.metrics = items.map((item) => ({
      left: item.offsetLeft - origin,
      width: item.offsetWidth,
    }));

    this.count = this.list.children.length;
    // The real list sits behind one leading copy.
    this.base = this.count;

    this.moveTo(this.centreFor(0), true);
    this.start();
  }

  /** Offset that puts item `i` of the real list in the middle of the viewport. */
  centreFor(i) {
    const item = this.metrics[this.base + i];
    if (!item) return 0;

    return item.left - (this.viewport.clientWidth - item.width) / 2;
  }

  start() {
    if (this.timer || this.reducedMotion) return;
    if (!this.hasAttribute('data-marquee')) return;

    this.timer = setInterval(this.advance, this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  advance() {
    this.index += 1;
    this.moveTo(this.centreFor(this.index), false);
  }

  onTransitionEnd(event) {
    if (event.propertyName !== 'transform') return;
    if (this.index < this.count) return;

    // Landed on the trailing copy's first item, which is pixel-identical to
    // the original: jump back so there is always another copy ahead.
    this.index = 0;
    this.moveTo(this.centreFor(0), true);
  }

  moveTo(offset, instant) {
    if (instant) this.track.style.transitionDuration = '0s';

    this.track.style.transform = `translateX(${-offset}px)`;

    if (instant) {
      // Flush the jump before restoring the transition, or the next step
      // would animate all the way back from the clone.
      void this.track.offsetHeight;
      this.track.style.transitionDuration = '';
    }
  }
}

if (!customElements.get('usp-bar-marquee')) {
  customElements.define('usp-bar-marquee', UspBarMarquee);
}
